import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import Swal from "sweetalert2";
import {
    Card, CardBody, Container, Row, Col, Label,
} from "reactstrap";
import { getTakeFrames, getTakeFramesByClinic, getClinic, deleteTakeFrame } from "service/api";

const THEME = "#DC4C64";

const cardTopStyle = {
    backgroundColor: THEME,
    padding: "6px 12px",
    color: "white",
    borderRadius: "4px 4px 0 0",
    fontWeight: 700,
    fontSize: 13,
};

const Listtakeframe = () => {
    const navigate = useNavigate();
    const [frames, setFrames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [clinicIdFromToken, setClinicIdFromToken] = useState(null);
    const [allcliniclist, setAllcliniclist] = useState([]);
    const [selectedClinic, setSelectedClinic] = useState("");
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 10;

    // ── Decode token ──
    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setClinicIdFromToken(decoded?.clinicID || null);
            } catch (e) { /* ignore */ }
        }
    }, []);

    // ── Admin: load clinic list ──
    useEffect(() => {
        if (!clinicIdFromToken) {
            getClinic().then(res => setAllcliniclist(res?.data || [])).catch(() => { });
        }
    }, [clinicIdFromToken]);

    // ── Fetch records ──
    const fetchFrames = async (clinicId) => {
        setLoading(true);
        try {
            let res;
            if (clinicId) {
                res = await getTakeFramesByClinic(clinicId);
            } else {
                res = await getTakeFrames();
            }
            setFrames(res?.data || []);
        } catch (e) {
            setFrames([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (clinicIdFromToken) {
            fetchFrames(clinicIdFromToken);
        } else {
            // Admin: load all initially
            fetchFrames(null);
        }
    }, [clinicIdFromToken]);

    const handleClinicFilter = (e) => {
        const val = e.target.value;
        setSelectedClinic(val);
        setCurrentPage(1);
        fetchFrames(val || null);
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: "Delete this frame record?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: THEME,
            cancelButtonColor: "#aaa",
            confirmButtonText: "Yes, delete",
        }).then(async (result) => {
            if (result.isConfirmed) {
                await deleteTakeFrame(id);
                setFrames(prev => prev.filter(f => f._id !== id));
                Swal.fire("Deleted!", "Frame record removed.", "success");
            }
        });
    };

    const getPatientName = (f) => {
        const p = f.visit_id?.patientid;
        if (p?.firstname || p?.lastname) {
            return `${p.title ? p.title + ". " : ""}${p.firstname || ""} ${p.lastname || ""}`.trim();
        }
        const v = f.visit_id;
        if (v?.firstname || v?.lastname) {
            return `${v.title ? v.title + ". " : ""}${v.firstname || ""} ${v.lastname || ""}`.trim();
        }
        return null;
    };

    const filtered = frames.filter(f => {
        const s = search.toLowerCase();
        const visitNo = f.visit_no || "";
        const brand = f.frame_brand || "";
        const patient = (getPatientName(f) || "").toLowerCase();
        return visitNo.toLowerCase().includes(s) || brand.toLowerCase().includes(s) || patient.includes(s);
    });

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    {/* Toolbar */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="page-title" style={{ margin: 0 }}>Frame Records</h4>
                        <Link to="/takeframe" className="btn btn-danger">
                            <i className="mdi mdi-plus me-1" />Add Frame
                        </Link>
                    </div>

                    <Card>
                        <div style={cardTopStyle}>Frame Selection Records</div>
                        <CardBody>
                            {/* Filters */}
                            <Row className="mb-3">
                                {!clinicIdFromToken && (
                                    <Col md={4}>
                                        <Label>Filter by Practice</Label>
                                        <select
                                            className="form-control"
                                            value={selectedClinic}
                                            onChange={handleClinicFilter}
                                        >
                                            <option value="">All Practices</option>
                                            {allcliniclist.map(c => (
                                                <option key={c._id} value={c._id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </Col>
                                )}
                                <Col md={4}>
                                    <Label>Search</Label>
                                    <input
                                        className="form-control"
                                        placeholder="Visit no., patient name, brand..."
                                        value={search}
                                        onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                                    />
                                </Col>
                            </Row>

                            {/* Table */}
                            {loading ? (
                                <div className="text-center py-4">
                                    <span className="spinner-border text-danger" />
                                </div>
                            ) : filtered.length === 0 ? (
                                <div className="text-center py-4 text-muted">No frame records found.</div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0" style={{ fontSize: 13 }}>
                                        <thead style={{ background: "#fff5f7" }}>
                                            <tr>
                                                <th style={{ color: "#000", fontWeight: 700 }}>#</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Visit No.</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Patient</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Practice</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Brand</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Color</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Size</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Material</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Type</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Shape</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Photo</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Date</th>
                                                <th style={{ color: "#000", fontWeight: 700 }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginated.map((f, i) => {
                                                const globalIndex = (currentPage - 1) * PAGE_SIZE + i;
                                                const clinic = f.visit_id?.patientid?.clinicname || f.clinic_id;
                                                return (
                                                    <tr key={f._id}>
                                                        <td style={{ color: "#888" }}>{globalIndex + 1}</td>
                                                        <td>
                                                            <span style={{ fontWeight: 700, color: THEME }}>
                                                                {f.visit_no || f.visit_id?.visit_no || "—"}
                                                            </span>
                                                        </td>
                                                        <td>{getPatientName(f) || "—"}</td>
                                                        <td>{clinic?.name || "—"}</td>
                                                        <td>{f.frame_brand || "—"}</td>
                                                        <td>{f.frame_color || "—"}</td>
                                                        <td>{f.frame_size || "—"}</td>
                                                        <td>{f.frame_material || "—"}</td>
                                                        <td>{f.frame_type || "—"}</td>
                                                        <td>{f.frame_shape || "—"}</td>
                                                        <td>
                                                            {f.frame_image
                                                                ? <img src={f.frame_image} alt="frame" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: `2px solid ${THEME}` }} />
                                                                : <span style={{ color: "#ccc" }}>—</span>}
                                                        </td>
                                                        <td style={{ color: "#888", fontSize: 11 }}>
                                                            {f.createdAt ? new Date(f.createdAt).toLocaleDateString("en-GB") : "—"}
                                                        </td>
                                                        <td style={{ whiteSpace: "nowrap" }}>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-1"
                                                                onClick={() => navigate(`/view-takeframe/${f._id}`)}
                                                                title="View"
                                                            >
                                                                <i className="mdi mdi-eye" />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDelete(f._id)}
                                                                title="Delete"
                                                            >
                                                                <i className="mdi mdi-delete" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Pagination */}
                            {!loading && filtered.length > PAGE_SIZE && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <span style={{ fontSize: 13, color: "#888" }}>
                                        Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length} records
                                    </span>
                                    <ul className="pagination mb-0" style={{ gap: 3 }}>
                                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                            <button className="page-link" style={{ color: THEME, borderColor: "#dee2e6" }} onClick={() => setCurrentPage(1)}>«</button>
                                        </li>
                                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                            <button className="page-link" style={{ color: THEME, borderColor: "#dee2e6" }} onClick={() => setCurrentPage(p => p - 1)}>‹</button>
                                        </li>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                                            .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                                            .reduce((acc, p, idx, arr) => {
                                                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                                                acc.push(p);
                                                return acc;
                                            }, [])
                                            .map((p, idx) =>
                                                p === "..." ? (
                                                    <li key={`ellipsis-${idx}`} className="page-item disabled">
                                                        <span className="page-link" style={{ borderColor: "#dee2e6" }}>…</span>
                                                    </li>
                                                ) : (
                                                    <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                                                        <button
                                                            className="page-link"
                                                            style={currentPage === p ? { backgroundColor: THEME, borderColor: THEME, color: "#fff" } : { color: THEME, borderColor: "#dee2e6" }}
                                                            onClick={() => setCurrentPage(p)}
                                                        >{p}</button>
                                                    </li>
                                                )
                                            )}
                                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                            <button className="page-link" style={{ color: THEME, borderColor: "#dee2e6" }} onClick={() => setCurrentPage(p => p + 1)}>›</button>
                                        </li>
                                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                            <button className="page-link" style={{ color: THEME, borderColor: "#dee2e6" }} onClick={() => setCurrentPage(totalPages)}>»</button>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Listtakeframe;
